import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { refreshSettings } from '../lib/siteSettings';

const API_URL = '';
const adminFetch = (url, options = {}) => fetch(url, { ...options, credentials: 'include' });

/**
 * Admin settings page. Renders a checklist grouped by section.
 * Currently exposes: livraison gratuite (bool + threshold).
 * New settings are added by extending _SETTING_DEFAULTS in the backend — the frontend
 * picks them up automatically from GET /api/bloom-gestion-secure/settings.
 */
export default function AdminSettings() {
  const [settings, setSettings] = useState([]);
  const [pending, setPending] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${API_URL}/api/bloom-gestion-secure/settings`);
      if (!res.ok) {
        toast.error('Impossible de charger les paramètres');
        return;
      }
      const data = await res.json();
      setSettings(data.settings || []);
      setPending({});
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const currentValue = (key) => (pending[key] !== undefined ? pending[key] : settings.find((s) => s.key === key)?.value);
  const isDirty = Object.keys(pending).length > 0;

  const save = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      const res = await adminFetch(`${API_URL}/api/bloom-gestion-secure/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: pending }),
      });
      if (!res.ok) {
        toast.error(`Échec (HTTP ${res.status})`);
        return;
      }
      toast.success('Paramètres enregistrés');
      await refreshSettings(); // refresh storefront cache
      await load();
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Group settings by their .group field
  const groups = settings.reduce((acc, s) => {
    (acc[s.group || 'Général'] = acc[s.group || 'Général'] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <SettingsIcon size={24} className="text-[#1A1514]" />
          <div>
            <h1 className="font-serif text-2xl">Paramètres</h1>
            <p className="text-xs text-[#6B6A68]">Active ou désactive les fonctionnalités du site.</p>
          </div>
        </div>
        <button
          onClick={save}
          disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium bg-[#1A1514] text-white hover:bg-[#2a2524] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          data-testid="admin-settings-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Enregistrer
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-[#6B6A68]" />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([groupName, items]) => (
            <div key={groupName} className="border border-[#E0DFDD] bg-white">
              <div className="px-5 py-3 bg-[#F9F8F6] border-b border-[#E0DFDD]">
                <h2 className="text-xs uppercase tracking-[0.15em] font-medium text-[#1A1514]">{groupName}</h2>
              </div>
              <div className="divide-y divide-[#E0DFDD]">
                {items.map((s) => (
                  <div
                    key={s.key}
                    className="px-5 py-4 flex items-start justify-between gap-6"
                    data-testid={`admin-setting-${s.key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm font-medium text-[#1A1514]">{s.label}</label>
                      {s.description && (
                        <p className="text-xs text-[#6B6A68] mt-1">{s.description}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {s.type === 'bool' ? (
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!currentValue(s.key)}
                            onChange={(e) => setPending((p) => ({ ...p, [s.key]: e.target.checked }))}
                            className="sr-only peer"
                            data-testid={`admin-setting-input-${s.key}`}
                          />
                          <div className="w-11 h-6 bg-[#E0DFDD] rounded-full peer peer-checked:bg-[#1A1514] transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
                        </label>
                      ) : s.type === 'number' ? (
                        <input
                          type="number"
                          value={currentValue(s.key) ?? ''}
                          onChange={(e) => setPending((p) => ({ ...p, [s.key]: e.target.value }))}
                          className="w-32 border border-[#E0DFDD] bg-white px-3 py-1.5 text-sm text-right focus:outline-none focus:border-[#1A1514]"
                          data-testid={`admin-setting-input-${s.key}`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={currentValue(s.key) ?? ''}
                          onChange={(e) => setPending((p) => ({ ...p, [s.key]: e.target.value }))}
                          className="w-64 border border-[#E0DFDD] bg-white px-3 py-1.5 text-sm focus:outline-none focus:border-[#1A1514]"
                          data-testid={`admin-setting-input-${s.key}`}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {settings.length === 0 && (
            <p className="text-center text-sm text-[#6B6A68] py-16">Aucun paramètre disponible.</p>
          )}
        </div>
      )}
    </div>
  );
}
